import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { env } from "@/config/env";
import { API_DEFAULTS } from "@/config/constants";
import { isSelectQueryResponse, transformSelectQueryResponse } from "./responseTransformer";

// Create base API client
const createApiClient = (tenantId?: string): AxiosInstance => {
  const client = axios.create({
    // In development, use relative path to leverage Vite proxy
    // In production, use the full API URL
    baseURL: env.DEV_MODE ? '' : env.API_URL,
    timeout: API_DEFAULTS.httpTimeout,
    headers: {
      "Content-Type": "application/json",
      "X-BFF-Internal-Key": "dev-bff-key",
      ...(tenantId && { "X-Tenant-ID": tenantId }),
    },
    withCredentials: true,
  });

  // Response interceptor
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Check if response contains a failure flag (Ecolane API pattern)
      if (!response.config?.skipApiFailureHandling && (response.data?.failure === true || response.data?.success === false)) {
        // ECOPD-14897: The Ecolane backend returns authentication errors as HTTP 200
        // with failure:true and error_class:"authentication_error" — e.g. after a
        // password change invalidates the current session.  Fire the session-expired
        // event so AuthProvider can attempt bootstrapSession() before anything redirects
        // to /login.  Honour skip401 on the config to suppress this for calls that
        // manage their own session recovery (e.g. the updateUser PUT itself).
        const isAuthError = response.data?.errors?.some(
          (e: { error_class?: string }) => e.error_class === 'authentication_error'
        );
        if (isAuthError && !response.config?.skip401) {
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }

        const errorMessage = response.data?.errors?.[0]?.detail || response.data?.errors?.[0]?.message || response.data?.message || "Operation failed";
        const error = new Error(errorMessage) as Error & { response?: AxiosResponse; isApiError?: boolean };
        error.response = response;
        error.isApiError = true;
        return Promise.reject(error);
      }

      // Auto-transform select_query responses
      // This handles response structure changes in ONE place
      if (isSelectQueryResponse(response.data)) {
        const transformed = transformSelectQueryResponse(response.data);
        response.data = transformed;
      }

      return response;
    },
    (error) => {
      // Handle common error responses
      if (error.response?.status === 401 && !error.config?.skip401) {
        // ECOPD-14897: Fire a custom event instead of hard-redirecting immediately.
        // AuthProvider listens and calls bootstrapSession() to confirm the RoR session
        // is truly gone before sending the user to /login.
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      }

      if (error.response?.status === 403) {
        const csrfErrorMessage =
          "Request blocked due to missing or invalid CSRF token.";
        if (error instanceof Error) {
          error.message = csrfErrorMessage;
        } else {
          error = new Error(csrfErrorMessage);
        }
      }

      return Promise.reject(error);
    },
  );

  // Request interceptor
  client.interceptors.request.use(
    (config) => config,
    (error) => Promise.reject(error),
  );

  return client;
};

// Default API client (without tenant)
export const apiClient = createApiClient();

// Create tenant-specific API client
export const createTenantApiClient = (tenantId: string): AxiosInstance => {
  return createApiClient(tenantId);
};

declare module "axios" {
  export interface AxiosRequestConfig {
    skip401?: boolean;
    skipApiFailureHandling?: boolean;
  }
}
// API response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: unknown;
}
